import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { LocationListing } from '@/services/locationListingService';
import { fetchCountries, fetchCounties, CountryOption, CountyOption } from '@/services/locationService';

const TYPE_OPTIONS = [
  { value: 'sauna', label: 'Sauna' },
  { value: 'plunge', label: 'Plunge' },
  { value: 'both', label: 'Sauna & Plunge' },
];

interface LocationFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: Partial<LocationListing>;
  onSubmit: (data: Partial<LocationListing>) => void;
}

export const LocationFormModal: React.FC<LocationFormModalProps> = ({ open, onOpenChange, initialData = {}, onSubmit }) => {
  const [form, setForm] = useState<Partial<LocationListing>>(initialData);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [counties, setCounties] = useState<CountyOption[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (open) {
      setForm(initialData || {});
    }
    // Only run when opening, or when the id changes (edit vs. create)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData?.id]);

  useEffect(() => {
    fetchCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (form.country) {
      fetchCounties(form.country as string).then(setCounties).catch(() => setCounties([]));
    } else {
      setCounties([]);
    }
  }, [form.country]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelect = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value, ...(name === 'country' ? { county: '' } : {}) }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.name) newErrors.name = 'Name is required';
    if (!form.country) newErrors.country = 'Country is required';
    if (!form.type) newErrors.type = 'Type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initialData?.id ? 'Edit Location' : 'Add Location'}</DialogTitle>
          <DialogDescription>
            {initialData?.id ? 'Update the location details.' : 'Fill in the details to add a new location.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block font-medium mb-1">Name</label>
            <Input
              id="name"
              name="name"
              placeholder="Name"
              value={form.name || ''}
              onChange={handleChange}
              required
              tabIndex={1}
              aria-label="Location Name"
            />
            {errors.name && <div className="text-red-500 text-xs mt-1">{errors.name}</div>}
          </div>
          <div>
            <label htmlFor="description" className="block font-medium mb-1">Description</label>
            <textarea
              id="description"
              name="description"
              placeholder="Description"
              value={form.description || ''}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border rounded p-2 min-h-[60px]"
              tabIndex={2}
              aria-label="Description"
            />
          </div>
          <div>
            <label htmlFor="country" className="block font-medium mb-1">Country</label>
            <Select value={form.country || ''} onValueChange={(v) => handleSelect('country', v)}>
              <SelectTrigger id="country" tabIndex={3} aria-label="Country">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.country && <div className="text-red-500 text-xs mt-1">{errors.country}</div>}
          </div>
          <div>
            <label htmlFor="county" className="block font-medium mb-1">County</label>
            <Select value={form.county || ''} onValueChange={(v) => handleSelect('county', v)} disabled={!form.country}>
              <SelectTrigger id="county" tabIndex={4} aria-label="County">
                <SelectValue placeholder="Select county" />
              </SelectTrigger>
              <SelectContent>
                {counties.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <fieldset className="flex gap-2" aria-label="Coordinates">
            <legend className="block font-medium mb-1">Coordinates</legend>
            <Input
              id="lat"
              name="lat"
              placeholder="Latitude"
              type="number"
              value={form.lat ?? ''}
              onChange={handleChange}
              step="any"
              tabIndex={5}
              aria-label="Latitude"
            />
            <Input
              id="lng"
              name="lng"
              placeholder="Longitude"
              type="number"
              value={form.lng ?? ''}
              onChange={handleChange}
              step="any"
              tabIndex={6}
              aria-label="Longitude"
            />
          </fieldset>
          <div>
            <label htmlFor="type" className="block font-medium mb-1">Type</label>
            <Select value={form.type || ''} onValueChange={(v) => handleSelect('type', v)}>
              <SelectTrigger id="type" tabIndex={7} aria-label="Type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.type && <div className="text-red-500 text-xs mt-1">{errors.type}</div>}
          </div>
          <div>
            <label htmlFor="services" className="block font-medium mb-1">Services</label>
            <Input
              id="services"
              name="services"
              placeholder="Services (comma separated)"
              value={Array.isArray(form.services) ? form.services.join(',') : form.services || ''}
              onChange={e => setForm(f => ({ ...f, services: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
              tabIndex={8}
              aria-label="Services"
            />
          </div>
          <div>
            <label htmlFor="location" className="block font-medium mb-1">Location (address or description)</label>
            <Input
              id="location"
              name="location"
              placeholder="Location (address or description)"
              value={form.location || ''}
              onChange={handleChange}
              tabIndex={9}
              aria-label="Location"
            />
          </div>
          <div>
            <label htmlFor="rating" className="block font-medium mb-1">Rating</label>
            <Input
              id="rating"
              name="rating"
              placeholder="Rating"
              type="number"
              value={form.rating ?? ''}
              onChange={handleChange}
              min={0}
              max={5}
              step={0.1}
              tabIndex={10}
              aria-label="Rating"
            />
          </div>
          <div>
            <label htmlFor="price" className="block font-medium mb-1">Price</label>
            <Input
              id="price"
              name="price"
              placeholder="Price"
              value={form.price || ''}
              onChange={handleChange}
              tabIndex={11}
              aria-label="Price"
            />
          </div>
          <div>
            <label htmlFor="image" className="block font-medium mb-1">Image URL</label>
            <Input
              id="image"
              name="image"
              placeholder="Image URL"
              value={form.image || ''}
              onChange={handleChange}
              tabIndex={12}
              aria-label="Image URL"
            />
          </div>
          <div>
            <label htmlFor="plan" className="block font-medium mb-1">Plan</label>
            <Input
              id="plan"
              name="plan"
              placeholder="Plan"
              value={form.plan || ''}
              onChange={handleChange}
              tabIndex={13}
              aria-label="Plan"
            />
          </div>
          <div>
            <label htmlFor="galleryImages" className="block font-medium mb-1">Gallery Images</label>
            <Input
              id="galleryImages"
              name="galleryImages"
              placeholder="Gallery Images (comma separated URLs)"
              value={Array.isArray(form.galleryImages) ? form.galleryImages.join(',') : form.galleryImages || ''}
              onChange={e => setForm(f => ({ ...f, galleryImages: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
              tabIndex={14}
              aria-label="Gallery Images"
            />
          </div>
          <fieldset className="border rounded p-2 space-y-2" aria-label="Operator">
            <legend className="font-semibold text-xs text-muted-foreground">Operator (optional)</legend>
            <div>
              <label htmlFor="operator.id" className="block font-medium mb-1">Operator ID</label>
              <Input
                id="operator.id"
                name="operator.id"
                placeholder="Operator ID"
                value={form.operator?.id || ''}
                onChange={e => setForm(f => ({ ...f, operator: { ...f.operator, id: e.target.value } }))}
                tabIndex={15}
                aria-label="Operator ID"
              />
            </div>
            <div>
              <label htmlFor="operator.name" className="block font-medium mb-1">Operator Name</label>
              <Input
                id="operator.name"
                name="operator.name"
                placeholder="Operator Name"
                value={form.operator?.name || ''}
                onChange={e => setForm(f => ({ ...f, operator: { ...f.operator, name: e.target.value } }))}
                tabIndex={16}
                aria-label="Operator Name"
              />
            </div>
            <div>
              <label htmlFor="operator.website" className="block font-medium mb-1">Operator Website</label>
              <Input
                id="operator.website"
                name="operator.website"
                placeholder="Operator Website"
                value={form.operator?.website || ''}
                onChange={e => setForm(f => ({ ...f, operator: { ...f.operator, website: e.target.value } }))}
                tabIndex={17}
                aria-label="Operator Website"
              />
            </div>
          </fieldset>
          <DialogFooter>
            <Button type="submit">{initialData?.id ? 'Save Changes' : 'Add Location'}</Button>
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </DialogClose>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}; 